import clsx from 'clsx';
import { type ReactNode, useMemo } from 'react';

import { Text } from '@dynatrace/strato-components/typography';

import {
  calendarDayRowCSS,
  calendarRowCSS,
  calendarWeekdaysCSS,
} from '../Calendar.sty.js';
import { Day } from '../Day.js';
import { useSelectDay } from '../hooks/useSelectDay.js';
import {
  NUMBER_OF_WEEK_ROWS,
  NUMBER_OF_WEEKDAYS,
} from '../types/calendarConfiguration.js';

/**
 * SelectDayView component
 * Renders a grid of buttons for selecting the day, with localized weekday labels
 */
export const SelectDayView = () => {
  const { weekdays, days, dispatch } = useSelectDay();

  const weekdaysToShow = useMemo(
    (): ReactNode =>
      weekdays.map((weekday, index) => (
        <th
          key={`${weekday.date}-${index}`}
          className="weekday"
          abbr={weekday.longName}
          scope="col"
        >
          <Text textStyle="small" as="p">
            {weekday.shortName}
          </Text>
        </th>
      )),
    [weekdays],
  );

  const daysToShow = useMemo(
    (): ReactNode =>
      new Array(NUMBER_OF_WEEK_ROWS).fill(0).map((_, index) => (
        <tr
          key={`${days[index].date}-row-${index}`}
          className={clsx([calendarRowCSS, calendarDayRowCSS])}
        >
          {days
            .slice(
              index * NUMBER_OF_WEEKDAYS,
              index * NUMBER_OF_WEEKDAYS + NUMBER_OF_WEEKDAYS,
            )
            .map((day, index) => (
              <Day
                date={day.date}
                position={index + 1}
                isCurrentMonthDay={day.isCurrentMonthDay}
                key={day.date.toISOString()}
              />
            ))}
        </tr>
      )),
    [days],
  );

  return (
    <>
      <thead>
        <tr
          className={clsx([
            calendarRowCSS,
            calendarDayRowCSS,
            calendarWeekdaysCSS,
          ])}
        >
          {weekdaysToShow}
        </tr>
      </thead>
      <tbody
        onMouseLeave={() => {
          dispatch({ type: 'hoveredItemChanged', payload: null });
        }}
      >
        {daysToShow}
      </tbody>
    </>
  );
};
SelectDayView['displayName'] = 'SelectDayView';
